import { useEffect } from 'react';

import { sizes } from 'core/components/theme/variables';
import { useSiteHeader } from 'core/components/layout/site-header-context';

// Scrolling down hides the site header so the quote gets the room. Bringing it back takes a longer
// upward scroll, so small course corrections don't keep exposing it. Near the top it always shows.
export const HIDE_AFTER_DOWNWARD_SCROLL = 8;
export const REVEAL_AFTER_UPWARD_SCROLL = 100;
export const ALWAYS_VISIBLE_ABOVE = 96;

const useHideSiteHeaderOnScroll = () => {
  const { scrollContainerRef, setSiteHeaderHidden } = useSiteHeader();

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) {
      return undefined;
    }

    // Elastic overscroll can report negative offsets, which would read as an upward scroll.
    let lastY = Math.max(0, scrollContainer.scrollTop);
    let isHidden = false;
    let downwardDistance = 0;
    let upwardDistance = 0;
    let frame = 0;

    const setHidden = (hidden) => {
      isHidden = hidden;
      downwardDistance = 0;
      upwardDistance = 0;
      setSiteHeaderHidden(hidden);
    };

    const evaluate = () => {
      frame = 0;
      const y = Math.max(0, scrollContainer.scrollTop);
      const delta = y - lastY;
      lastY = y;

      if (y <= ALWAYS_VISIBLE_ABOVE) {
        if (isHidden) {
          setHidden(false);
        }
        return;
      }

      // Track distance rather than a single event's delta, so a slow trackpad gesture counts as one
      // scroll. Reversing direction resets the other direction's distance.
      if (delta > 0) {
        downwardDistance += delta;
        upwardDistance = 0;
        // Hiding the header shortens the scrollable range by its height. On a page that only just
        // scrolls, that would pull the position back into the always-visible zone and bring the
        // header straight back, so leave it shown there.
        const scrollRange = scrollContainer.scrollHeight - scrollContainer.clientHeight;
        const hasRoomToHide = scrollRange - sizes.headerHeight > ALWAYS_VISIBLE_ABOVE;
        if (!isHidden && hasRoomToHide && downwardDistance >= HIDE_AFTER_DOWNWARD_SCROLL) {
          setHidden(true);
        }
      } else if (delta < 0) {
        // Hiding the header makes the scroll area taller, and at the bottom of the page the browser
        // pulls the scroll position back to fit. That lands exactly on the bottom; a real upward
        // scroll moves away from it.
        const atBottom = y + scrollContainer.clientHeight >= scrollContainer.scrollHeight - 1;
        if (atBottom) {
          return;
        }
        upwardDistance -= delta;
        downwardDistance = 0;
        if (isHidden && upwardDistance >= REVEAL_AFTER_UPWARD_SCROLL) {
          setHidden(false);
        }
      }
    };

    const onScroll = () => {
      if (!frame) {
        frame = window.requestAnimationFrame(evaluate);
      }
    };

    scrollContainer.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      scrollContainer.removeEventListener('scroll', onScroll);
      if (frame) {
        window.cancelAnimationFrame(frame);
      }
      // Leaving the page brings the site header back for whatever renders next.
      setSiteHeaderHidden(false);
    };
  }, [scrollContainerRef, setSiteHeaderHidden]);
};

export default useHideSiteHeaderOnScroll;
